/**
 * Стол до начала розыгрыша: в центре внимания — участники за столом и кнопка участия.
 * Сверху — состояние сбора (сколько подтвердили, обратный отсчёт), затем места за столом
 * карточками (свободные — пунктиром) и краткое описание розыгрыша, которое можно раскрыть.
 */
import type { RoomView } from '@mt/protocol';
import { cr, crn, secondsLeft } from '../lib/format';
import { client, useApp } from '../net/client';
import { Avatar, pct, signedCr, toneOf } from './kit';

export function WaitingRoom({ room, now }: { room: RoomView; now: number }) {
  const balance = useApp((s) => s.profile?.balance ?? 0);
  const left = secondsLeft(room.nextStartAt, now);
  const need = Math.max(0, room.minPlayers - room.ready);
  const empty = Math.max(0, room.maxPlayers - room.seats.length);
  const enough = balance >= room.ante;
  const share = Math.min(1, room.ready / room.minPlayers);

  return (
    <div className="waiting-room">
      <section className="panel wr-hero">
        <div className="wr-status">
          <div className={`wr-ring ${room.status}`} style={{ ['--p' as string]: room.status === 'countdown' ? 1 : share }} aria-hidden="true">
            <span className="mono">{room.status === 'countdown' ? left : `${room.ready}/${room.minPlayers}`}</span>
          </div>
          <div>
            <h2>{room.status === 'countdown' ? `Розыгрыш начнётся через ${left} с` : room.status === 'forming' ? (need > 0 ? `Ждём ещё ${need} ${plural(need, 'подтверждение', 'подтверждения', 'подтверждений')}` : 'Все готовы') : 'Подготовка следующего розыгрыша'}</h2>
            <p className="muted-line">
              Стол {cr(room.ante)} · {room.minPlayers}–{room.maxPlayers} игроков · за столом {room.seats.length}, готовы {room.ready}
              {room.status === 'forming' && need > 0 ? '. Если людей не хватит, стол доберут боты (они помечены).' : '.'}
            </p>
          </div>
        </div>
        <div className="wr-cta">
          {!room.meSeated ? (
            <p className="muted-line">Вы наблюдаете за столом.</p>
          ) : room.meReady ? (
            <>
              <p className="wr-confirmed">
                <b>✓ Участие подтверждено.</b> Взнос {cr(room.ante)} спишется при старте.
              </p>
              <button className="btn ghost" onClick={() => client.ready(false)}>
                Отменить участие
              </button>
            </>
          ) : (
            <>
              <button className="btn primary xl" disabled={!enough} onClick={() => client.ready(true)}>
                Участвовать · взнос {cr(room.ante)}
              </button>
              <p className="muted-line small">{enough ? `Ваш баланс ${cr(balance)}` : 'Недостаточно кредитов для взноса.'}</p>
            </>
          )}
        </div>
      </section>

      {room.notice && <p className="notice warn">{room.notice}</p>}

      <section className="seat-grid" aria-label="Места за столом">
        {room.seats.map((s) => (
          <article key={s.playerId} className={`seat-card ${s.ready ? 'ready' : ''} ${s.isMe ? 'me' : ''} ${s.connected ? '' : 'offline'}`}>
            <Avatar id={s.playerId} name={s.name} size={44} bot={s.isBot} />
            <div className="sc-body">
              <b className="sc-name">
                {s.name}
                {s.isMe && <em className="tag me">вы</em>}
                {s.isBot && <em className="tag">бот</em>}
              </b>
              <span className="sc-meta mono">{crn(s.balance)} кр.</span>
              {s.stats && (
                <span className="seat-stats" title={`розыгрышей: ${s.stats.games}`}>
                  <span>WR {pct(s.stats.winRate)}</span>
                  <span className={toneOf(s.stats.net)}>{signedCr(s.stats.net)}</span>
                </span>
              )}
            </div>
            <span className={`sc-state ${s.ready ? 'ok' : ''}`}>{!s.connected ? 'нет связи' : s.ready ? 'готов' : 'ожидает'}</span>
          </article>
        ))}
        {Array.from({ length: empty }, (_, i) => (
          <article key={'e' + i} className="seat-card empty">
            <span className="avatar empty" aria-hidden="true" />
            <span className="dim">свободно</span>
          </article>
        ))}
      </section>

      <details className="panel wr-rules">
        <summary>Как проходит розыгрыш</summary>
        <ol className="flow">
          <li>
            <b>Подготовка</b>
            <span>взнос {cr(room.ante)} в банк, хеш состава опубликован</span>
          </li>
          <li>
            <b>Круг 1 · график</b>
            <span>без шкалы цен; выбор направления</span>
          </li>
          <li>
            <b>Круг 2 · шкала</b>
            <span>цены и пункты; направление можно сменить</span>
          </li>
          <li>
            <b>Круг 3 · состав</b>
            <span>три акции раскрыты; только число пунктов</span>
          </li>
          <li>
            <b>Целевая свеча</b>
            <span>P₁ — закрытие следующей минуты</span>
          </li>
          <li>
            <b>Расчёт</b>
            <span>выигрывают угадавшие направление; доля — по точности</span>
          </li>
        </ol>
      </details>
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
